// src/routes/terms.tsx
import { createFileRoute } from '@tanstack/react-router';
import { createRouteConfig } from '../utils/routeGuard';
import { useTranslation } from 'react-i18next';
import { staticI18n } from '../i18n';

// adjust paths as needed:
import { LegalLayout } from '@/components/LegalLayout';
import { LegalSection } from '@/components/LegalSection';

// same SEO pattern as your current page
const seoTitle = staticI18n.t('terms.seo.title');
const seoDescription = staticI18n.t('terms.seo.description');
const seoOgTitle = staticI18n.t('terms.seo.ogTitle');
const seoOgDescription = staticI18n.t('terms.seo.ogDescription');

export const Route = createFileRoute('/terms')({
  component: TermsPage,
  ...createRouteConfig({
    isPublic: true,
    seo: {
      title: seoTitle,
      description: seoDescription,
      ogTitle: seoOgTitle,
      ogDescription: seoOgDescription,
    },
  }),
});

type TermsSection = {
  title: string;
  content?: string[]; // paragraphs
  list?: string[]; // bullets, optional
  html?: string; // optional preformatted HTML (links etc.)
  email?: string; // optional contact email
  contactText?: string; // label before email
};

function TermsPage() {
  const { t } = useTranslation();

  const title = t('terms.title');
  const lastUpdated = t('terms.lastUpdated');
  const introduction = t('terms.introduction');

  const sections =
    (t('terms.sections', { returnObjects: true }) as TermsSection[]) ?? [];

  return (
    <LegalLayout title={title}>
      {/* Intro block (not included in TOC because no title prop) */}
      <div className='text-sm text-gray-600'>{lastUpdated}</div>
      <p className='mt-2 text-gray-700'>{introduction}</p>

      {sections.map((s, i) => (
        <LegalSection
          key={i}
          id={s.title
            .toLowerCase()
            .replace(/\s+/g, '-')
            .replace(/[^a-z0-9\-]/g, '')}
          title={s.title}
          html={s.html ?? null}
        >
          {!s.html && (
            <div className='space-y-3'>
              {(s.content ?? []).map((p, idx) => (
                <p key={idx}>{p}</p>
              ))}

              {!!s.list?.length && (
                <ul className='list-disc ml-5 space-y-1'>
                  {s.list.map((li, j) => (
                    <li key={j}>{li}</li>
                  ))}
                </ul>
              )}

              {s.email && (
                <p className='pt-1'>
                  {s.contactText}{' '}
                  <a
                    className='text-blue-600 underline hover:text-blue-800'
                    href={`mailto:${s.email}`}
                  >
                    {s.email}
                  </a>
                </p>
              )}
            </div>
          )}
        </LegalSection>
      ))}
    </LegalLayout>
  );
}
